import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Heading2 from '../../components/common/headings/heading2';
import Heading3 from '../../components/common/headings/heading3';
import { parameters, strings } from '../../localization/strings';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import { useSite } from '../../hooks/use-site';
import { useAuth } from '../../hooks/use-auth';
import { getLocalizedPath } from '../../helpers/localization-helper';
import * as ordersApi from '../../services/api/orders';
import ShopifyRegisterForm from '../../components/users/shopify-register-form';

function ShopifyWelcomePageComponent() {
  const { orderHash: orderHashParam, shopifyOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { region: urlRegion, locale } = useSite();
  const { token } = useAuth();

  const email = searchParams.get('email');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<ordersApi.ShopifyOrderDetails | null>(null);
  const [orderHash, setOrderHash] = useState<string | null>(orderHashParam || null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Skip if already initialized or if we already have order details
    if (hasInitialized.current || orderDetails) return;
    hasInitialized.current = true;

    const fetchOrderDetails = async () => {
      let hashToUse = orderHashParam || null;

      // If we have shopifyOrderId, first lookup the orderHash
      if (shopifyOrderId) {
        if (!email) {
          setError('Missing email parameter');
          setIsLoading(false);

          return;
        }

        try {
          const shopifyData = await ordersApi.getOrderByShopifyId(shopifyOrderId, email);

          hashToUse = shopifyData.orderHash;
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Order not found');
          setIsLoading(false);

          return;
        }
      }

      if (!hashToUse) {
        setError('Invalid order');
        setIsLoading(false);

        return;
      }

      try {
        const details = await ordersApi.getShopifyDetails(hashToUse);
        const orderRegion = details.region;
        const orderLocale = details.locale;

        // Redirect to correct region/locale URL if not already there
        if (urlRegion !== orderRegion || locale !== orderLocale) {
          // Strip existing region/locale prefix before adding correct one
          let basePath = window.location.pathname;

          if (urlRegion) {
            basePath = basePath.replace(new RegExp(`^/${urlRegion}(/${locale})?`), '');
          } else if (locale && locale !== 'lt') {
            basePath = basePath.replace(new RegExp(`^/${locale}`), '');
          }

          const correctPath = getLocalizedPath(basePath, orderLocale, orderRegion);
          const queryString = email ? `?email=${encodeURIComponent(email)}` : '';

          window.location.href = `${correctPath}${queryString}`;

          return;
        }

        // If user is already logged in, link orders and redirect to dashboard
        if (token) {
          try {
            await ordersApi.linkOrderToCustomer(token, hashToUse);

            navigate(getLocalizedPath(parameters().dashboard, orderLocale, orderRegion));

            return;
          } catch {
            // If linking fails, continue with normal flow
          }
        }

        if (details.hasAccount) {
          navigate(
            `${getLocalizedPath(
              parameters().login,
              orderLocale,
              orderRegion,
            )}?orderHash=${hashToUse}&email=${encodeURIComponent(details.email)}`,
          );

          return;
        }

        setOrderHash(hashToUse);
        setOrderDetails(details);
        setIsLoading(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load order');
        setIsLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderHashParam, shopifyOrderId, email, navigate, urlRegion, token, locale]);

  if (isLoading) {
    return <CommonAnimatedLoader />;
  }

  if (error) {
    return (
      <div className="min-h-full flex items-center justify-center py-10 px-4">
        <AlertPrimary type="danger" title={error} />
      </div>
    );
  }

  // Once loaded, use order's region/locale for all navigation
  const orderRegion = orderDetails?.region || 'eu';
  const orderLocale = orderDetails?.locale || 'de';

  return (
    <div className="min-h-full md:flex items-center justify-center py-10 md:py-20 px-4 sm:px-6 lg:px-8 mx-auto max-w-7xl relative">
      <div className="md:w-3/5 z-10 relative border-b md:border-b-0 pb-10 md:pb-0 md:border-r border-gray-200 md:pr-20">
        <Heading2 className="mb-4">{strings().shopify?.welcomeTitle || 'Thank you for your order!'}</Heading2>
        <p className="text-content-600 mb-8">
          {strings().shopify?.welcomeSubtitle || 'Create your account to get started with your test.'}
        </p>
        {orderDetails && shopifyOrderId && (
          <ShopifyRegisterForm
            orderHash={orderHash!}
            shopifyOrderId={shopifyOrderId}
            orderDetails={orderDetails}
            onLoadingChange={setIsSubmitting}
          />
        )}
      </div>

      {!isSubmitting && (
        <div className="mt-10 md:mt-0 md:w-2/5 relative z-10 md:pl-20">
          <Heading3 className="mb-6">{strings().registration.haveAccount}</Heading3>
          <NavLink
            key="login"
            to={`${getLocalizedPath(parameters().login, orderLocale, orderRegion)}?orderHash=${orderHash}`}
            className="!no-underline bg-content-300 text-content-900 hover:bg-content-400 inline-flex px-7 sm:px-9 text-base rounded-2xl py-3.5 font-bold"
          >
            {strings().registration.login}
          </NavLink>
        </div>
      )}
    </div>
  );
}

export default ShopifyWelcomePageComponent;
